import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { DELIVERY_CHANNEL_CONFIG } from '@shared/api/giftCoupon/giftCoupon.interface';
import {
  addconErrorMessage,
  clearAddconMapping,
  fetchAddconEnvironment,
  fetchAddconGoodsCached,
  isAddconUnsupported,
  setAddconMapping,
} from '@shared/api/giftCouponAddcon/giftCouponAddcon.controller';
import {
  AddconEnvironment,
  AddconGoods,
  AddconMappingTarget,
  AddconMappingUpdate,
  GIFT_PRODUCT_DELIVERY_CHANNELS,
  GiftProductDeliveryChannel,
} from '@shared/api/giftCouponAddcon/giftCouponAddcon.interface';
import { Link } from 'react-router-dom';

import {
  AddconGoodsTable,
  addconSendTypeLabel,
  addconValidLabel,
  formatAddconPrice,
} from './AddconGoodsTable';

const CHANNEL_LABEL: Record<GiftProductDeliveryChannel, string> = {
  Addcon: '애드콘 — API로 자동 발송',
  Officecon: `${DELIVERY_CHANNEL_CONFIG.Officecon.label} — 수동 발송`,
  GiftiShow: `${DELIVERY_CHANNEL_CONFIG.GiftiShow.label} — 수동 발송`,
  EumGift: `${DELIVERY_CHANNEL_CONFIG.EumGift.label} — 수동 발송`,
};

export interface AddconMappingPanelProps {
  target: AddconMappingTarget;
  /** 저장된 문서 id. 없으면(신규) 먼저 저장하라고 안내만 한다 */
  id?: string;
  /** 서버에 저장된 현재 값 */
  deliveryChannel?: GiftProductDeliveryChannel;
  noReq?: string;
  /** 매핑 쓰기는 Admin만 */
  canEdit: boolean;
  /** 저장 결과를 호스트 상태에 되돌려 준다(목록 atom 갱신 등) */
  onSaved?: (next: {
    deliveryChannel: GiftProductDeliveryChannel | null;
    noReq: string | null;
  }) => void;
  /** 선택형 원상품처럼 이 문서에는 연결이 의미 없을 때 안내만 */
  disabledReason?: string;
}

type GoodsState =
  | { status: 'idle' | 'loading' }
  | { status: 'ready'; goods: AddconGoods[] }
  | { status: 'unsupported' }
  | { status: 'error'; message: string };

/**
 * 상품 ↔ 애드콘 연결 패널 (선택지 / 단일 쿠폰 원상품 공용).
 * 폼 저장과 별개로 여기서 바로 저장(upsert)된다. 상품명·단가·유효기간은 저장하지 않고 중개서버 조회값만 보여 준다.
 */
export const AddconMappingPanel: FC<AddconMappingPanelProps> = ({
  target,
  id,
  deliveryChannel,
  noReq,
  canEdit,
  onSaved,
  disabledReason,
}) => {
  const [channel, setChannel] = useState<GiftProductDeliveryChannel | ''>(
    deliveryChannel ?? '',
  );
  const [code, setCode] = useState(noReq ?? '');
  const [savedChannel, setSavedChannel] = useState<GiftProductDeliveryChannel | ''>(
    deliveryChannel ?? '',
  );
  const [savedCode, setSavedCode] = useState(noReq ?? '');
  const [goodsState, setGoodsState] = useState<GoodsState>({ status: 'idle' });
  const [environment, setEnvironment] = useState<AddconEnvironment | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(
    null,
  );

  //* 서버 값이 늦게 오면(단건 조회 완료) 그때 한 번 맞춘다 — 편집 중인 값은 건드리지 않는다
  useEffect(() => {
    setSavedChannel(deliveryChannel ?? '');
    setSavedCode(noReq ?? '');
    setChannel((prev) => (prev === '' && deliveryChannel ? deliveryChannel : prev));
    setCode((prev) => (prev === '' && noReq ? noReq : prev));
  }, [deliveryChannel, noReq]);

  const loadGoods = useCallback(async (force = false) => {
    setGoodsState({ status: 'loading' });
    try {
      const [goods, env] = await Promise.all([
        fetchAddconGoodsCached(force),
        fetchAddconEnvironment().catch(() => null),
      ]);
      setGoodsState({ status: 'ready', goods });
      setEnvironment(env);
    } catch (error) {
      if (isAddconUnsupported(error)) {
        setGoodsState({ status: 'unsupported' });
        return;
      }
      setGoodsState({
        status: 'error',
        message: addconErrorMessage(error, '계약 상품을 불러오지 못했다.'),
      });
    }
  }, []);

  //* 애드콘을 고른 순간부터 상품 목록이 필요하다
  useEffect(() => {
    if (channel === 'Addcon' && goodsState.status === 'idle') void loadGoods();
  }, [channel, goodsState.status, loadGoods]);

  const goods = useMemo(
    () => (goodsState.status === 'ready' ? goodsState.goods : []),
    [goodsState],
  );
  const matched = useMemo(
    () => goods.find((row) => row.noReq === code.trim()) ?? null,
    [goods, code],
  );
  const dirty = channel !== savedChannel || code.trim() !== savedCode;
  const needsCode = channel === 'Addcon' && !code.trim();
  const unsupported = goodsState.status === 'unsupported';

  const save = async () => {
    if (!id || busy) return;
    if (needsCode) {
      setMessage({ kind: 'error', text: '애드콘으로 보내려면 계약번호가 필요하다.' });
      return;
    }
    const trimmed = code.trim();
    if (savedChannel === 'Addcon' && channel === 'Addcon' && !trimmed) {
      setMessage({
        kind: 'error',
        text: '애드콘인 채로 계약번호만 지울 수는 없다. 발송 매체를 먼저 바꾼다.',
      });
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      const nextChannel = channel || null;
      //* 한 요청에 바뀐 것만 — null은 삭제, 생략은 그대로 (서버 계약)
      const body: AddconMappingUpdate = {};
      if (channel !== savedChannel) body.deliveryChannel = nextChannel;
      if (trimmed !== savedCode) body.noReq = trimmed || null;
      const result = await setAddconMapping(target, id, body);
      setSavedChannel(result.deliveryChannel ?? '');
      setSavedCode(result.addcon?.noReq ?? '');
      setChannel(result.deliveryChannel ?? '');
      setCode(result.addcon?.noReq ?? '');
      onSaved?.({
        deliveryChannel: result.deliveryChannel ?? null,
        noReq: result.addcon?.noReq ?? null,
      });
      setMessage({
        kind: 'ok',
        text:
          nextChannel === 'Addcon'
            ? `저장됐다. 이 상품의 쿠폰은 애드콘 API로 발송된다${
                matched ? ` (${matched.goodsName ?? trimmed})` : ''
              }.`
            : nextChannel
            ? `저장됐다. 수동 발송(${DELIVERY_CHANNEL_CONFIG[nextChannel].label}) 상품이다.`
            : '저장됐다. 발송 매체 미설정(수동) 상품이다.',
      });
    } catch (error) {
      setMessage({
        kind: 'error',
        text: isAddconUnsupported(error)
          ? '백엔드에 애드콘 연결 기능이 아직 없다(미배포 서버).'
          : addconErrorMessage(error, '저장하지 못했다.'),
      });
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    if (!id || busy) return;
    if (!window.confirm('애드콘 연결을 해제하고 발송 매체를 미설정(수동)으로 돌릴까요?'))
      return;
    setBusy(true);
    setMessage(null);
    try {
      await clearAddconMapping(target, id);
      setChannel('');
      setCode('');
      setSavedChannel('');
      setSavedCode('');
      onSaved?.({ deliveryChannel: null, noReq: null });
      setMessage({ kind: 'ok', text: '해제됐다. 이제 수동 발송 상품이다.' });
    } catch (error) {
      setMessage({
        kind: 'error',
        text: isAddconUnsupported(error)
          ? '백엔드에 애드콘 연결 기능이 아직 없다(미배포 서버).'
          : addconErrorMessage(error, '해제하지 못했다.'),
      });
    } finally {
      setBusy(false);
    }
  };

  const statusChip = (() => {
    if (savedChannel === 'Addcon' && savedCode) {
      return {
        text: '애드콘 API 발송',
        className: 'bg-green-50 text-green-800 ring-green-200',
      };
    }
    if (savedChannel === 'Addcon') {
      return {
        text: '애드콘 — 계약번호 없음',
        className: 'bg-amber-50 text-amber-800 ring-amber-200',
      };
    }
    if (savedChannel) {
      return {
        text: `수동 발송 · ${DELIVERY_CHANNEL_CONFIG[savedChannel].label}`,
        className: 'bg-gray-100 text-gray-700 ring-gray-200',
      };
    }
    return {
      text: '미설정 (수동 발송)',
      className: 'bg-gray-100 text-gray-500 ring-gray-200',
    };
  })();

  const readOnly = !canEdit || !id || !!disabledReason || busy;
  const fieldClass =
    'w-full min-w-0 rounded border border-gray-300 bg-white px-3 py-2 text-sm disabled:bg-gray-50 disabled:text-gray-500';

  return (
    <section
      className="rounded-md border border-gray-200 bg-white p-5 sm:p-6"
      aria-labelledby="addcon-mapping-title"
    >
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 id="addcon-mapping-title" className="text-base font-semibold">
            발송 매체 · 애드콘 연결
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            이 상품의 쿠폰을 어디로 보내는지. 애드콘이면 발송처리 때 API로 자동 발행된다.
          </p>
        </div>
        <span
          className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ring-1 ${statusChip.className}`}
        >
          {statusChip.text}
        </span>
      </div>

      {disabledReason ? (
        <p className="rounded bg-gray-50 px-3 py-2 text-sm text-gray-600">
          {disabledReason}
        </p>
      ) : !id ? (
        <p className="rounded bg-gray-50 px-3 py-2 text-sm text-gray-600">
          먼저 상품을 저장한 뒤 수정 화면에서 연결할 수 있다.
        </p>
      ) : (
        <div className="space-y-4">
          {!canEdit && (
            <p className="rounded bg-gray-50 px-3 py-2 text-xs text-gray-600">
              관리자만 바꿀 수 있다 (조회만).
            </p>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="min-w-0">
              <label
                htmlFor="addcon-channel"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                발송 매체
              </label>
              <select
                id="addcon-channel"
                value={channel}
                disabled={readOnly}
                onChange={(event) => {
                  setChannel(event.target.value as GiftProductDeliveryChannel | '');
                  setMessage(null);
                }}
                className={fieldClass}
              >
                <option value="">미설정 — 수동 발송</option>
                {GIFT_PRODUCT_DELIVERY_CHANNELS.map((value) => (
                  <option key={value} value={value}>
                    {CHANNEL_LABEL[value]}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                발송처리 모달의 기본값으로 쓰이고, 애드콘일 때만 "API 발송" 버튼이 열린다.
              </p>
            </div>
            {channel === 'Addcon' && (
              <div className="min-w-0">
                <div className="mb-1 flex items-center justify-between">
                  <label
                    htmlFor="addcon-noreq"
                    className="text-sm font-medium text-gray-700"
                  >
                    애드콘 계약번호
                  </label>
                  <button
                    type="button"
                    disabled={readOnly || unsupported}
                    onClick={() => setPickerOpen((prev) => !prev)}
                    className="rounded border border-indigo-300 bg-white px-2 py-0.5 text-xs text-indigo-700 hover:bg-indigo-50 disabled:opacity-40"
                  >
                    {pickerOpen ? '목록 닫기' : '상품에서 고르기'}
                  </button>
                </div>
                <input
                  id="addcon-noreq"
                  value={code}
                  disabled={readOnly}
                  onChange={(event) => {
                    setCode(event.target.value);
                    setMessage(null);
                  }}
                  placeholder="예: 50078"
                  spellCheck={false}
                  className={`${fieldClass} font-mono`}
                />
                {goodsState.status === 'loading' && (
                  <p className="mt-1 text-xs text-gray-500">계약 상품 확인 중…</p>
                )}
                {unsupported && (
                  <p className="mt-1 text-xs text-amber-800">
                    백엔드에 애드콘 연결 기능이 아직 없다(미배포 서버). 번호는 적어 둘 수
                    있지만 저장은 실패한다.
                  </p>
                )}
                {goodsState.status === 'error' && (
                  <p className="mt-1 text-xs text-red-700">
                    {goodsState.message}{' '}
                    <button
                      type="button"
                      className="underline"
                      onClick={() => void loadGoods(true)}
                    >
                      다시 시도
                    </button>
                  </p>
                )}
                {goodsState.status === 'ready' &&
                  code.trim() &&
                  (matched ? (
                    <div className="mt-2 flex items-center gap-3 rounded border border-green-200 bg-green-50 p-2 text-xs text-green-900">
                      {matched.goodsImage && (
                        <img
                          src={matched.goodsImage}
                          alt=""
                          className="h-10 w-10 shrink-0 rounded object-cover"
                        />
                      )}
                      <div className="min-w-0">
                        <p className="font-semibold">
                          {matched.goodsName ?? matched.nmReq}
                          {matched.companyName && (
                            <span className="ml-1 font-normal text-green-800">
                              · {matched.companyName}
                            </span>
                          )}
                        </p>
                        <p>
                          {formatAddconPrice(matched.cpnPrice)} · 유효{' '}
                          {addconValidLabel(matched)} · 문자{' '}
                          {addconSendTypeLabel(matched.sendType)}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <p className="mt-1 text-xs text-red-700">
                      계약 상품 목록에 없는 번호다. 저장하면 서버가 거절한다.
                    </p>
                  ))}
              </div>
            )}
          </div>

          {channel === 'Addcon' && pickerOpen && goodsState.status !== 'unsupported' && (
            <div className="rounded border border-gray-200 bg-gray-50 p-3">
              <AddconGoodsTable
                goods={goods}
                loading={goodsState.status === 'loading'}
                error={goodsState.status === 'error' ? goodsState.message : null}
                onRetry={() => void loadGoods(true)}
                selectedNoReq={code.trim()}
                onSelect={(row) => {
                  setCode(row.noReq);
                  setPickerOpen(false);
                  setMessage(null);
                }}
                compact
                autoFocus
              />
              <p className="mt-2 text-[11px] text-gray-500">
                전체 목록은{' '}
                <Link
                  to="/gift-api/goods"
                  className="underline"
                  target="_blank"
                  rel="noreferrer"
                >
                  애드콘 계약 상품 페이지
                </Link>
                에서도 볼 수 있다.
              </p>
            </div>
          )}

          {environment?.messageDelivery === 'none' && channel === 'Addcon' && (
            <p className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              지금 백엔드는 테스트 중개서버다. 애드콘 발송이 성공해도 실제 문자는 가지
              않는다.
            </p>
          )}

          {message && (
            <p
              role={message.kind === 'error' ? 'alert' : 'status'}
              className={`rounded px-3 py-2 text-sm ${
                message.kind === 'error'
                  ? 'bg-red-50 text-red-700'
                  : 'bg-green-50 text-green-800'
              }`}
            >
              {message.text}
            </p>
          )}

          {canEdit && (
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => void save()}
                disabled={busy || !dirty}
                className="rounded bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {busy ? '저장 중…' : '연결 저장'}
              </button>
              {(savedCode || savedChannel) && (
                <button
                  type="button"
                  onClick={() => void clear()}
                  disabled={busy}
                  className="rounded border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                >
                  연결 해제
                </button>
              )}
              <span className="text-xs text-gray-500">
                {dirty
                  ? '아직 저장 안 됨 — 폼 저장과 별개로 여기서 바로 저장된다.'
                  : '저장됨'}
              </span>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default AddconMappingPanel;
